import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Shield,
  Users,
  Rocket,
  DollarSign,
  Zap,
  Flag,
  Star,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Settings,
  FileText,
  BarChart3,
  Home,
  Globe,
  Code,
  Server,
  Database,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { adminAPI, projectAPI } from '../lib/api';
import { formatNumber, formatCurrency, formatRelativeTime } from '../lib/utils';

const TABS = [
  { id: 'overview', icon: BarChart3, labelKey: 'admin.tabOverview' },
  { id: 'settings', icon: Settings, labelKey: 'admin.tabSettings' },
  { id: 'pages', icon: FileText, labelKey: 'admin.tabPages' },
  { id: 'homepage', icon: Home, labelKey: 'admin.tabHomepage' },
  { id: 'system', icon: Server, labelKey: 'admin.tabSystem' },
];

export function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Overview data
  const [analytics, setAnalytics] = useState(null);
  const [flaggedProjects, setFlaggedProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.is_admin) {
      navigate('/');
      return;
    }
    fetchOverviewData();
  }, [isAuthenticated, user, navigate]);

  const fetchOverviewData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, flaggedRes, projectsRes] = await Promise.all([
        adminAPI.getAnalytics(),
        adminAPI.getFlagged(),
        projectAPI.list({ feed: 'trending', limit: 50 })
      ]);
      setAnalytics(analyticsRes.data);
      setFlaggedProjects(flaggedRes.data.flagged_projects);
      setAllProjects(projectsRes.data.projects);
    } catch (error) {
      toast.error(t('admin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOverviewData();
    setRefreshing(false);
    toast.success(t('admin.refreshed'));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{t('admin.title')}</h1>
                <p className="text-slate-600 text-sm">{t('admin.subtitle')}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {t('admin.refresh')}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <OverviewTab
            analytics={analytics}
            flaggedProjects={flaggedProjects}
            allProjects={allProjects}
            onRefresh={fetchOverviewData}
            t={t}
          />
        )}
        {activeTab === 'settings' && <SettingsTab t={t} />}
        {activeTab === 'pages' && <PagesTab t={t} />}
        {activeTab === 'homepage' && <HomepageTab t={t} />}
        {activeTab === 'system' && <SystemTab t={t} />}
      </div>
    </div>
  );
}

// ==================== OVERVIEW TAB ====================

function OverviewTab({ analytics, flaggedProjects, allProjects, onRefresh, t }) {
  return (
    <>
      {/* Analytics Cards */}
      <div className="admin-grid mb-8">
        <AnalyticsCard
          icon={Users}
          label={t('admin.totalUsers')}
          value={formatNumber(analytics?.users?.total || 0)}
          subtext={`+${analytics?.users?.new_today || 0} ${t('admin.today')}`}
          color="blue"
        />
        <AnalyticsCard
          icon={Rocket}
          label={t('admin.totalProjects')}
          value={formatNumber(analytics?.projects?.total || 0)}
          subtext={`+${analytics?.projects?.new_today || 0} ${t('admin.today')}`}
          color="green"
        />
        <AnalyticsCard
          icon={Zap}
          label={t('admin.totalBoosts')}
          value={formatNumber(analytics?.boosts?.total || 0)}
          subtext={`+${analytics?.boosts?.today || 0} ${t('admin.today')}`}
          color="orange"
        />
        <AnalyticsCard
          icon={DollarSign}
          label={t('admin.totalRaised')}
          value={formatCurrency(analytics?.revenue?.total || 0)}
          color="emerald"
        />
      </div>

      {/* Boost Breakdown */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8">
        <h2 className="font-bold text-slate-900 mb-4">{t('admin.boostBreakdown')}</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-xl">
            <div className="text-2xl font-bold text-blue-600">
              {formatNumber(analytics?.boosts?.free || 0)}
            </div>
            <div className="text-sm text-blue-700">{t('admin.freeBoosts')}</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-xl">
            <div className="text-2xl font-bold text-green-600">
              {formatNumber(analytics?.boosts?.time || 0)}
            </div>
            <div className="text-sm text-green-700">{t('admin.timeBoosts')}</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-xl">
            <div className="text-2xl font-bold text-orange-600">
              {formatNumber(analytics?.boosts?.money || 0)}
            </div>
            <div className="text-sm text-orange-700">{t('admin.moneyBoosts')}</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Flagged Content */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-500" />
              {t('admin.flaggedContent')}
            </h2>
            <Badge variant="destructive">{flaggedProjects.length}</Badge>
          </div>

          {flaggedProjects.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {flaggedProjects.map((project) => (
                <FlaggedProjectCard
                  key={project.project_id}
                  project={project}
                  onAction={onRefresh}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
              {t('admin.noFlagged')}
            </div>
          )}
        </div>

        {/* Smart Picks Management */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Star className="w-5 h-5 text-purple-500" />
              {t('admin.smartPicks')}
            </h2>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {allProjects.slice(0, 10).map((project) => (
              <SmartPickCard
                key={project.project_id}
                project={project}
                onAction={onRefresh}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ==================== SETTINGS TAB ====================

function SettingsTab({ t }) {
  const [settings, setSettings] = useState({
    site_name: '',
    logo_url: '',
    seo_title: '',
    seo_description: '',
    google_analytics_id: '',
    google_verification_code: '',
    custom_css: '',
    custom_head_scripts: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await adminAPI.getSettings();
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      toast.error(t('admin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success(t('admin.settingsSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Site Info */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-500" />
          {t('admin.siteInfo')}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.siteName')}</label>
            <Input
              value={settings.site_name}
              onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
              placeholder="Fikkr"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.logoUrl')}</label>
            <Input
              value={settings.logo_url}
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
              placeholder="https://example.com/logo.png"
            />
            <p className="text-xs text-slate-400 mt-1">{t('admin.logoUrlHint')}</p>
            {settings.logo_url && (
              <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">{t('admin.logoPreview')}</p>
                <img src={settings.logo_url} alt="Logo" className="h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.seoTitle')}</label>
            <Input
              value={settings.seo_title}
              onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })}
              placeholder="Fikkr - Launch Ideas. Build Momentum."
            />
            <p className="text-xs text-slate-400 mt-1">{t('admin.seoTitleHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.seoDescription')}</label>
            <Textarea
              value={settings.seo_description}
              onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })}
              placeholder="A platform where community engagement matters..."
              rows={3}
            />
            <p className="text-xs text-slate-400 mt-1">{t('admin.seoDescHint')}</p>
          </div>
        </div>
      </div>

      {/* Google Codes */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Code className="w-5 h-5 text-green-500" />
          {t('admin.googleCodes')}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.gaId')}</label>
            <Input
              value={settings.google_analytics_id}
              onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
              placeholder="G-XXXXXXXXXX"
            />
            <p className="text-xs text-slate-400 mt-1">{t('admin.gaHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.verificationCode')}</label>
            <Input
              value={settings.google_verification_code}
              onChange={(e) => setSettings({ ...settings, google_verification_code: e.target.value })}
              placeholder="google-site-verification=..."
            />
            <p className="text-xs text-slate-400 mt-1">{t('admin.verificationHint')}</p>
          </div>
        </div>
      </div>

      {/* Custom Code */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Code className="w-5 h-5 text-purple-500" />
          {t('admin.customCode')}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.customCss')}</label>
            <Textarea
              value={settings.custom_css}
              onChange={(e) => setSettings({ ...settings, custom_css: e.target.value })}
              placeholder="/* Custom CSS */"
              rows={4}
              className="font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.customHeadScripts')}</label>
            <Textarea
              value={settings.custom_head_scripts}
              onChange={(e) => setSettings({ ...settings, custom_head_scripts: e.target.value })}
              placeholder="<!-- Custom scripts for <head> -->"
              rows={4}
              className="font-mono text-sm"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="btn-boost">
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {t('admin.saveSettings')}
        </Button>
      </div>
    </div>
  );
}

// ==================== PAGES TAB ====================

function PagesTab({ t }) {
  const [activePage, setActivePage] = useState('terms');
  const [pageData, setPageData] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const pages = [
    { slug: 'terms', labelKey: 'admin.pageTerms', icon: FileText },
    { slug: 'privacy', labelKey: 'admin.pagePrivacy', icon: Shield },
    { slug: 'kvkk', labelKey: 'admin.pageKvkk', icon: FileText },
  ];

  useEffect(() => {
    fetchPage(activePage);
  }, [activePage]);

  const fetchPage = async (slug) => {
    setLoading(true);
    try {
      const res = await adminAPI.getPage(slug);
      setPageData({ title: res.data.title || '', content: res.data.content || '' });
    } catch (err) {
      setPageData({ title: '', content: '' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!pageData.title.trim() || !pageData.content.trim()) {
      toast.error(t('admin.fillRequired'));
      return;
    }
    setSaving(true);
    try {
      await adminAPI.updatePage(activePage, pageData);
      toast.success(t('admin.pageSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Selector */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex gap-2">
          {pages.map((page) => (
            <button
              key={page.slug}
              onClick={() => setActivePage(page.slug)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activePage === page.slug
                  ? 'bg-orange-100 text-orange-700'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <page.icon className="w-4 h-4" />
              {t(page.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Page Editor */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.pageTitle')}</label>
              <Input
                value={pageData.title}
                onChange={(e) => setPageData({ ...pageData, title: e.target.value })}
                placeholder={t('admin.pageTitlePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('admin.pageContent')}
                <span className="text-xs text-slate-400 ml-2">{t('admin.htmlSupported')}</span>
              </label>
              <Textarea
                value={pageData.content}
                onChange={(e) => setPageData({ ...pageData, content: e.target.value })}
                placeholder={t('admin.pageContentPlaceholder')}
                rows={20}
                className="font-mono text-sm"
              />
            </div>
          </div>

          <div className="flex justify-between items-center mt-6">
            <p className="text-xs text-slate-400">
              {t('admin.pagePreviewHint')}
            </p>
            <Button onClick={handleSave} disabled={saving} className="btn-boost">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {t('admin.savePage')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== HOMEPAGE TAB ====================

function HomepageTab({ t }) {
  const [settings, setSettings] = useState({
    hero_title1: '',
    hero_highlight: '',
    hero_title2: '',
    hero_subtitle: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await adminAPI.getSettings();
      setSettings(prev => ({
        hero_title1: res.data.hero_title1 || '',
        hero_highlight: res.data.hero_highlight || '',
        hero_title2: res.data.hero_title2 || '',
        hero_subtitle: res.data.hero_subtitle || '',
      }));
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success(t('admin.homepageSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
          <Home className="w-5 h-5 text-orange-500" />
          {t('admin.heroSection')}
        </h2>
        <p className="text-sm text-slate-500 mb-6">{t('admin.heroDesc')}</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.heroTitle1')}</label>
            <Input
              value={settings.hero_title1}
              onChange={(e) => setSettings({ ...settings, hero_title1: e.target.value })}
              placeholder={t('landing.title1')}
            />
            <p className="text-xs text-slate-400 mt-1">{t('admin.heroTitle1Hint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.heroHighlight')}</label>
            <Input
              value={settings.hero_highlight}
              onChange={(e) => setSettings({ ...settings, hero_highlight: e.target.value })}
              placeholder={t('landing.titleHighlight')}
            />
            <p className="text-xs text-slate-400 mt-1">{t('admin.heroHighlightHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.heroTitle2')}</label>
            <Input
              value={settings.hero_title2}
              onChange={(e) => setSettings({ ...settings, hero_title2: e.target.value })}
              placeholder={t('landing.title2')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.heroSubtitle')}</label>
            <Textarea
              value={settings.hero_subtitle}
              onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
              placeholder={t('landing.subtitle')}
              rows={3}
            />
          </div>
        </div>

        {/* Preview */}
        <div className="mt-6 p-6 bg-slate-50 rounded-xl border border-slate-200">
          <p className="text-xs text-slate-400 mb-3 uppercase font-medium">{t('admin.preview')}</p>
          <h2 className="text-2xl font-bold text-slate-900">
            {settings.hero_title1 || t('landing.title1')}
            <span className="text-gradient">{settings.hero_highlight || t('landing.titleHighlight')}</span>
            {settings.hero_title2 || t('landing.title2')}
          </h2>
          <p className="text-slate-600 mt-2">
            {settings.hero_subtitle || t('landing.subtitle')}
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="btn-boost">
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {t('admin.saveHomepage')}
        </Button>
      </div>
    </div>
  );
}

// ==================== SYSTEM TAB ====================

function SystemTab({ t }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await adminAPI.getSystemStatus();
      setStatus(res.data);
    } catch (err) {
      toast.error(t('admin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Server Info */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Server className="w-5 h-5 text-blue-500" />
          {t('admin.serverInfo')}
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <InfoRow label={t('admin.status')} value={status?.server?.uptime_info || 'Unknown'} badge="green" />
          <InfoRow label={t('admin.pythonVersion')} value={status?.server?.python_version?.split(' ')[0] || '-'} />
          <InfoRow label={t('admin.platform')} value={status?.server?.platform || '-'} />
        </div>
      </div>

      {/* Database Info */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-green-500" />
          {t('admin.databaseInfo')}
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <InfoRow label={t('admin.dbSize')} value={`${status?.database?.size_mb || 0} MB`} />
          <InfoRow label={t('admin.collections')} value={status?.database?.collections || 0} />
          <InfoRow label={t('admin.totalDocs')} value={formatNumber(status?.database?.total_documents || 0)} />
        </div>
      </div>

      {/* Collection Counts */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-4">{t('admin.collectionCounts')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label={t('admin.totalUsers')} value={formatNumber(status?.counts?.users || 0)} color="blue" />
          <StatCard label={t('admin.totalProjects')} value={formatNumber(status?.counts?.projects || 0)} color="green" />
          <StatCard label={t('admin.messages')} value={formatNumber(status?.counts?.messages || 0)} color="orange" />
          <StatCard label={t('admin.notifications')} value={formatNumber(status?.counts?.notifications || 0)} color="purple" />
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="outline" onClick={fetchStatus}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('admin.refresh')}
        </Button>
      </div>
    </div>
  );
}

// ==================== SHARED COMPONENTS ====================

function InfoRow({ label, value, badge }) {
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-sm font-medium text-slate-900 flex items-center gap-2">
        {badge && (
          <span className={`w-2 h-2 rounded-full ${badge === 'green' ? 'bg-green-500' : 'bg-red-500'}`} />
        )}
        {value}
      </span>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    orange: 'bg-orange-50 text-orange-700',
    purple: 'bg-purple-50 text-purple-700',
  };

  return (
    <div className={`p-4 rounded-xl text-center ${colors[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs mt-1">{label}</div>
    </div>
  );
}

function AnalyticsCard({ icon: Icon, label, value, subtext, color }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    emerald: 'bg-emerald-100 text-emerald-600',
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm text-slate-600">{label}</span>
      </div>
      <div className="text-3xl font-bold text-slate-900">{value}</div>
      {subtext && <p className="text-sm text-green-600 mt-1">{subtext}</p>}
    </div>
  );
}

function FlaggedProjectCard({ project, onAction }) {
  const [loading, setLoading] = useState(false);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await adminAPI.flagProject(project.project_id, {
        reason: project.flag_reason || 'Admin action',
        action
      });
      toast.success(`Project ${action}ged successfully`);
      onAction();
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-red-50 rounded-xl border border-red-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/project/${project.project_id}`}
            className="font-semibold text-slate-900 hover:text-orange-600 truncate block"
          >
            {project.title}
          </Link>
          <p className="text-sm text-slate-600 truncate">{project.tagline}</p>
          <p className="text-xs text-red-600 mt-1">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            {project.flag_reason || 'Flagged for review'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAction('unflag')}
            disabled={loading}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => handleAction('archive')}
            disabled={loading}
          >
            Archive
          </Button>
        </div>
      </div>
    </div>
  );
}

function SmartPickCard({ project, onAction }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const isSmartPick = project.status === 'smart_pick';

  const handleToggle = async () => {
    setLoading(true);
    try {
      await adminAPI.toggleSmartPick(project.project_id, {
        is_smart_pick: !isSmartPick,
        curator_notes: notes || null
      });
      toast.success(isSmartPick ? 'Removed from Smart Picks' : 'Added to Smart Picks');
      setDialogOpen(false);
      onAction();
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
          <img
            src={project.image_url || 'https://images.pexels.com/photos/3912477/pexels-photo-3912477.jpeg'}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <Link
            to={`/project/${project.project_id}`}
            className="font-medium text-slate-900 hover:text-orange-600 truncate block text-sm"
          >
            {project.title}
          </Link>
          <div className="flex items-center gap-2 mt-0.5">
            <TrendingUp className="w-3 h-3 text-orange-500" />
            <span className="text-xs text-slate-500">
              {formatNumber(Math.round(project.momentum_score))} momentum
            </span>
          </div>
        </div>
        <Button
          size="sm"
          variant={isSmartPick ? 'secondary' : 'outline'}
          onClick={() => setDialogOpen(true)}
          className={isSmartPick ? 'bg-purple-100 text-purple-700' : ''}
        >
          <Star className={`w-3 h-3 mr-1 ${isSmartPick ? 'fill-purple-500' : ''}`} />
          {isSmartPick ? 'Picked' : 'Pick'}
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isSmartPick ? 'Remove from Smart Picks?' : 'Add to Smart Picks'}
            </DialogTitle>
            <DialogDescription>
              {isSmartPick
                ? 'This will remove the project from the curated Smart Picks feed.'
                : 'Add curator notes to explain why this project was selected.'}
            </DialogDescription>
          </DialogHeader>

          {!isSmartPick && (
            <Textarea
              placeholder="Curator notes (optional)..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleToggle} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isSmartPick ? 'Remove' : 'Add to Smart Picks'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
